/**
 * Per-purchase thank-you pages.
 *
 * Every paid flow lands on its own URL so Meta Ads and Google Ads can build
 * URL-based conversion rules without depending on event plumbing:
 *
 *   /subscription/success-trial       ₹99 trial course
 *   /subscription/success-quarterly   quarterly membership
 *   /subscription/success-annual      annual membership
 *   /subscription/success-assessment  assessment fee (first or retake)
 *   /subscription/success-report      report unlock
 *
 * The purchase events themselves still fire in the Razorpay handlers right
 * after server-side verification (deterministic Meta `event_id`, webhook
 * backstop), so these pages fire NO purchase event — only the normal
 * route-change `page_view`.
 */

export type PurchaseSuccessVariant =
  | "trial"
  | "pass"
  | "quarterly"
  | "annual"
  | "assessment"
  | "report";

export const PURCHASE_SUCCESS_PATH: Record<PurchaseSuccessVariant, string> = {
  trial: "/subscription/success-trial",
  pass: "/subscription/success-pass",
  quarterly: "/subscription/success-quarterly",
  annual: "/subscription/success-annual",
  assessment: "/subscription/success-assessment",
  report: "/subscription/success-report",
};

/** Legacy `/subscription/success/{kind}` values → new variant. */
export function variantFromLegacyPlanKind(planKind?: string): PurchaseSuccessVariant {
  return planKind === "annual" ? "annual" : "quarterly";
}

export interface PurchaseSuccessDetails {
  planName?: string;
  amount?: number;
  currency?: string;
  childName?: string;
  /** Trial only — where "Start the course" goes. */
  courseId?: string;
  courseTitle?: string;
  /** Assessment / report only — where the follow-up CTA goes. */
  sessionId?: string;
  assessmentType?: string;
  /** Razorpay payment id — dedupes the thank-you page custom event. */
  transactionId?: string;
}

const STORAGE_KEY = "leadxkids:purchaseSuccessDetails";

/** Survives a reload of the thank-you page (router state does not). */
export function storePurchaseSuccessDetails(
  variant: PurchaseSuccessVariant,
  details: PurchaseSuccessDetails,
): void {
  try {
    sessionStorage.setItem(STORAGE_KEY, JSON.stringify({ variant, details }));
  } catch {
    // Router state still carries the details for the immediate navigation.
  }
}

export function readPurchaseSuccessDetails(
  variant: PurchaseSuccessVariant,
): PurchaseSuccessDetails {
  try {
    const raw = sessionStorage.getItem(STORAGE_KEY);
    if (!raw) return {};
    const parsed = JSON.parse(raw) as { variant?: string; details?: PurchaseSuccessDetails };
    if (parsed?.variant !== variant) return {};
    return parsed.details ?? {};
  } catch {
    return {};
  }
}
